import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
	ChevronRight,
	ChevronDown,
	Folder,
	FolderOpen,
	MoreVertical,
	FolderPlus,
	ArrowDownAZ,
	Trash2,
	X,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { useServiceCatalogStore } from "../../../store/ServiceCatalog";
import { ServiceCatalogFolder } from "../../../store/ServiceCatalog/types";
import { ServiceCatalogItemRow } from "./service-catalog-item-row.component";

function countFolderContents(folder: ServiceCatalogFolder): {
	items: number;
	folders: number;
} {
	let items = folder.items.length;
	let folders = folder.folders.length;
	for (const sub of folder.folders) {
		const subCounts = countFolderContents(sub);
		items += subCounts.items;
		folders += subCounts.folders;
	}
	return { items, folders };
}

interface ServiceCatalogFolderNodeProps {
	folder: ServiceCatalogFolder;
	parentId: string | null;
	index: number;
	depth: number;
	collapseSignal?: number;
	expandSignal?: number;
}

export const ServiceCatalogFolderNode: React.FC<
	ServiceCatalogFolderNodeProps
> = ({ folder, parentId, index, depth, collapseSignal, expandSignal }) => {
	const [expanded, setExpanded] = useState(true);
	const initialRender = useRef(true);

	useEffect(() => {
		if (initialRender.current) return;
		if (collapseSignal) setExpanded(false);
	}, [collapseSignal]);

	useEffect(() => {
		if (initialRender.current) return;
		if (expandSignal) setExpanded(true);
	}, [expandSignal]);

	useEffect(() => {
		initialRender.current = false;
	}, []);

	const [isRenaming, setIsRenaming] = useState(false);
	const [renameValue, setRenameValue] = useState(folder.name);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const addFolder = useServiceCatalogStore((s) => s.addFolder);
	const renameFolder = useServiceCatalogStore((s) => s.renameFolder);
	const deleteFolder = useServiceCatalogStore((s) => s.deleteFolder);
	const removeItem = useServiceCatalogStore((s) => s.removeItem);
	const sortFolderAlphabetically = useServiceCatalogStore(
		(s) => s.sortFolderAlphabetically
	);

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		isOver,
	} = useSortable({
		id: folder.id,
		data: { type: "folder", parentId, index },
	});

	const { active } = useDndContext();
	const showFolderDropHighlight =
		isOver && active?.data.current?.type === "browser-item";

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const handleStartRename = useCallback(() => {
		setIsRenaming(true);
		setRenameValue(folder.name);
		setTimeout(() => inputRef.current?.select(), 0);
	}, [folder.name]);

	const handleFinishRename = useCallback(() => {
		const trimmed = renameValue.trim();
		if (trimmed && trimmed !== folder.name) {
			renameFolder(folder.id, trimmed);
		}
		setIsRenaming(false);
	}, [renameValue, folder.name, folder.id, renameFolder]);

	const handleRenameKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") handleFinishRename();
			if (e.key === "Escape") setIsRenaming(false);
		},
		[handleFinishRename]
	);

	const handleAddSubfolder = useCallback(() => {
		addFolder(folder.id, "Neuer Unterordner");
		setExpanded(true);
	}, [addFolder, folder.id]);

	const handleDelete = useCallback(() => {
		const { items, folders } = countFolderContents(folder);
		if (items === 0 && folders === 0) {
			deleteFolder(folder.id);
		} else {
			setShowDeleteConfirm(true);
		}
	}, [deleteFolder, folder]);

	const handleConfirmDelete = useCallback(() => {
		deleteFolder(folder.id);
		setShowDeleteConfirm(false);
	}, [deleteFolder, folder.id]);

	const handleSort = useCallback(() => {
		sortFolderAlphabetically(folder.id);
	}, [sortFolderAlphabetically, folder.id]);

	const handleRemoveItem = useCallback(
		(itemIndex: number) => {
			removeItem(folder.id, itemIndex);
		},
		[removeItem, folder.id]
	);

	const { setNodeRef: setTailRef, isOver: isTailOver } = useDroppable({
		id: `tail-${folder.id}`,
		data: {
			type: "item-tail",
			parentId: folder.id,
			index: folder.items.length,
		},
	});

	const showTailDropIndicator = isTailOver && !!active;

	const isEmpty = folder.folders.length === 0 && folder.items.length === 0;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="select-none"
			data-testid={`catalog-folder-${folder.id}`}
		>
			<div
				{...attributes}
				{...listeners}
				className={`flex items-center gap-1 py-1 px-1 rounded group transition-colors cursor-grab ${
					showFolderDropHighlight
						? "bg-blue-50 ring-1 ring-blue-400"
						: "hover:bg-muted/50"
				}`}
				style={{ paddingLeft: depth * 20 + 4 }}
			>
				<button
					onClick={() => setExpanded(!expanded)}
					className="p-0.5"
					aria-label={
						expanded ? "Ordner zuklappen" : "Ordner aufklappen"
					}
				>
					{expanded ? (
						<ChevronDown className="h-4 w-4 text-muted-foreground" />
					) : (
						<ChevronRight className="h-4 w-4 text-muted-foreground" />
					)}
				</button>

				{expanded ? (
					<FolderOpen className="h-4 w-4 text-amber-600 shrink-0" />
				) : (
					<Folder className="h-4 w-4 text-amber-600 shrink-0" />
				)}

				{isRenaming ? (
					<Input
						ref={inputRef}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
						onBlur={handleFinishRename}
						onKeyDown={handleRenameKeyDown}
						className="h-6 text-sm py-0 px-1 w-48"
						autoFocus
						data-testid="catalog-folder-rename-input"
					/>
				) : (
					<span
						className="text-sm font-medium truncate cursor-pointer flex-1 min-w-0"
						onDoubleClick={handleStartRename}
						title={folder.name}
					>
						{folder.name}
					</span>
				)}

				<span className="text-xs text-muted-foreground ml-auto mr-1">
					{folder.items.length > 0 && `${folder.items.length}`}
				</span>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
							aria-label="Ordner-Aktionen"
							data-testid={`catalog-folder-actions-${folder.id}`}
						>
							<MoreVertical className="h-3.5 w-3.5" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={handleStartRename}>
							Umbenennen
						</DropdownMenuItem>
						<DropdownMenuItem onClick={handleAddSubfolder}>
							<FolderPlus className="h-4 w-4 mr-2" />
							Unterordner erstellen
						</DropdownMenuItem>
						<DropdownMenuItem onClick={handleSort}>
							<ArrowDownAZ className="h-4 w-4 mr-2" />
							Alphabetisch sortieren
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={handleDelete}
							className="text-destructive"
						>
							<Trash2 className="h-4 w-4 mr-2" />
							Ordner löschen
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{expanded && (
				<div>
					<SortableContext
						items={folder.folders.map((f) => f.id)}
						strategy={verticalListSortingStrategy}
					>
						{folder.folders.map((sub, i) => (
							<ServiceCatalogFolderNode
								key={sub.id}
								folder={sub}
								parentId={folder.id}
								index={i}
								depth={depth + 1}
								collapseSignal={collapseSignal}
								expandSignal={expandSignal}
							/>
						))}
					</SortableContext>

					<SortableContext
						items={folder.items.map(
							(_, i) => `item-${folder.id}-${i}`
						)}
						strategy={verticalListSortingStrategy}
					>
						{folder.items.map((item, i) => (
							<ServiceCatalogItemRow
								key={`${folder.id}-item-${i}`}
								item={item}
								folderId={folder.id}
								index={i}
								depth={depth + 1}
								onRemove={() => handleRemoveItem(i)}
							/>
						))}
					</SortableContext>

					<div
						ref={setTailRef}
						className="relative"
						style={{ paddingLeft: (depth + 1) * 20 + 24 }}
					>
						{showTailDropIndicator && (
							<div
								className="absolute top-0 right-0 h-0.5 bg-blue-500 z-10"
								style={{ left: (depth + 1) * 20 + 24 }}
							/>
						)}
						{isEmpty ? (
							<div className="text-xs text-muted-foreground py-2 italic">
								Positionen hierhin ziehen
							</div>
						) : (
							<div className="h-2" />
						)}
					</div>
				</div>
			)}

			<AlertDialog
				open={showDeleteConfirm}
				onOpenChange={setShowDeleteConfirm}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Ordner löschen</AlertDialogTitle>
						<AlertDialogDescription>
							{(() => {
								const { items, folders } =
									countFolderContents(folder);
								const parts: string[] = [];
								if (folders > 0)
									parts.push(`${folders} Unterordner`);
								if (items > 0)
									parts.push(`${items} Positionen`);
								return `Dieser Ordner enthält ${parts.join(
									" und "
								)}. Möchten Sie den Ordner und alle Inhalte löschen?`;
							})()}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
